import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Header from './Header';
import { API_URL, getImageUrl, normalizeList } from '../config';
import SellerBankKycFields, {
  emptySellerBankForm,
  isSellerBankFormStarted,
  validateSellerBankForm,
  appendSellerBankFormData,
} from './SellerBankKycFields';
import './ManageSellersPage.css';

const sellersApiUrl = `${API_URL}/Sellers`;

/** Build selectable items at Sub, Tertiary, Quaternary levels for category checkboxes. */
function flattenCategorySelections(categories, normalizeList) {
  const items = [];
  normalizeList(categories).forEach((c) => {
    normalizeList(c?.subCategories).forEach((s) => {
      items.push({ type: 'sub', id: s.subCategoryId, path: `${c.categoryName} › ${s.subCategoryName}` });
      normalizeList(s?.tertiaryCategories).forEach((t) => {
        items.push({ type: 'tertiary', id: t.tertiaryCategoryId, path: `${c.categoryName} › ${s.subCategoryName} › ${t.tertiaryCategoryName}` });
        normalizeList(t?.quaternaryCategories).forEach((q) => {
          items.push({ type: 'quaternary', id: q.quaternaryCategoryId, path: `${c.categoryName} › ${s.subCategoryName} › ${t.tertiaryCategoryName} › ${q.quaternaryCategoryName}` });
        });
      });
    });
  });
  return items;
}

const ManageSellersPage = () => {
  const navigate = useNavigate();
  const isLoggedIn = useSelector((state) => state.auth.isLoggedIn);
  const userRole = useSelector((state) => state.auth.userRole);

  const [sellers, setSellers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryTree, setCategoryTree] = useState([]);

  // Edit Modal State
  const [editModal, setEditModal] = useState(false);
  const [editingSeller, setEditingSeller] = useState(null);
  const [editForm, setEditForm] = useState({
    username: '',
    email: '',
    password: '',
    isActive: true,
    commissionPercent: '',
    shopName: '',
    gstNumber: '',
    ownerName: '',
    shopPhotoUrl: '',
    shopMapUrl: '',
    shopAddress: '',
    phoneNumber: '',
    alternatePhoneNumber: '',
    address: '',
    city: '',
    state: '',
    pinCode: '',
    location: ''
  });
  const [editSubCategoryIds, setEditSubCategoryIds] = useState([]);
  const [editTertiaryCategoryIds, setEditTertiaryCategoryIds] = useState([]);
  const [editQuaternaryCategoryIds, setEditQuaternaryCategoryIds] = useState([]);
  const [editLoading, setEditLoading] = useState(false);
  const [editError, setEditError] = useState(null);
  const [editFetching, setEditFetching] = useState(false);
  const [shopPhotoUploading, setShopPhotoUploading] = useState(false);
  const [shopPhotoPreview, setShopPhotoPreview] = useState('');
  const [bankForm, setBankForm] = useState(emptySellerBankForm());
  const [passbookFile, setPassbookFile] = useState(null);
  const [passbookPreview, setPassbookPreview] = useState('');
  const [existingPassbookUrl, setExistingPassbookUrl] = useState('');

  // Delete Modal State
  const [deleteModal, setDeleteModal] = useState(false);
  const [deletingSeller, setDeletingSeller] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [statusLoadingId, setStatusLoadingId] = useState(null);

  const getAuthHeader = () => {
    const token = localStorage.getItem('jwtToken');
    return { Authorization: `Bearer ${token}` };
  };

  useEffect(() => {
    if (!isLoggedIn || userRole !== 'Admin') {
      navigate('/');
      return;
    }
    fetchSellers();
  }, [isLoggedIn, userRole, navigate]);

  useEffect(() => {
    axios.get(`${API_URL}/Categories/with-subcategories`)
      .then((res) => setCategoryTree(normalizeList(res.data)))
      .catch(() => setCategoryTree([]));
  }, []);

  const fetchSellers = async () => {
    try {
      setLoading(true);
      setError(null);
      console.log('Fetching sellers from:', sellersApiUrl);
      console.log('Auth header:', getAuthHeader());
      const response = await axios.get(sellersApiUrl, { headers: getAuthHeader() });
      console.log('Sellers response:', response.data);
      const data = response.data;
      const sellersArray = Array.isArray(data) ? data : (data?.$values ?? []);
      setSellers(sellersArray);
    } catch (err) {
      console.error('Error fetching sellers:', err);
      const errorMessage = err.response?.status === 401 
        ? 'Unauthorized. Please log in again.'
        : err.response?.status === 403
          ? 'Access denied. Admin role required.'
          : err.response?.data?.message || err.message || 'Failed to load sellers';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const sellerIsActive = (seller) => seller?.isActive !== false && seller?.IsActive !== false;

  const getSellerCode = (seller) =>
    String(
      seller?.sellerCode ??
      seller?.SellerCode ??
      seller?.referralCode ??
      seller?.ReferralCode ??
      ''
    ).trim();

  const filteredSellers = sellers.filter((seller) => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) return true;
    const code = getSellerCode(seller).toLowerCase();
    return (
      code.includes(term) ||
      seller.username?.toLowerCase().includes(term) ||
      seller.email?.toLowerCase().includes(term)
    );
  });

  // Edit functions
  const openEditModal = async (seller) => {
    setEditingSeller(seller);
    setEditForm({
      username: seller.username,
      email: seller.email,
      password: '',
      isActive: sellerIsActive(seller),
      commissionPercent: seller.commissionPercent != null ? String(seller.commissionPercent) : '',
      shopName: '',
      gstNumber: '',
      ownerName: '',
      shopPhotoUrl: '',
      shopMapUrl: '',
      shopAddress: '',
      phoneNumber: '',
      alternatePhoneNumber: '',
      address: '',
      city: '',
      state: '',
      pinCode: '',
      location: ''
    });
    setEditSubCategoryIds([]);
    setEditTertiaryCategoryIds([]);
    setEditQuaternaryCategoryIds([]);
    setEditError(null);
    setEditModal(true);
    setEditFetching(true);
    setShopPhotoPreview((prev) => {
      if (prev && String(prev).startsWith('blob:')) URL.revokeObjectURL(prev);
      return '';
    });
    try {
      const res = await axios.get(`${sellersApiUrl}/${seller.userId}`, { headers: getAuthHeader() });
      const data = res.data;
      setEditForm(prev => ({
        ...prev,
        username: data.username ?? prev.username,
        email: data.email ?? prev.email,
        isActive: data.isActive ?? prev.isActive,
        commissionPercent: data.commissionPercent != null ? String(data.commissionPercent) : prev.commissionPercent,
        shopName: data.shopName ?? '',
        gstNumber: data.gstNumber ?? '',
        ownerName: data.ownerName ?? '',
        shopPhotoUrl: data.shopPhotoUrl ?? '',
        shopMapUrl: data.shopMapUrl ?? '',
        shopAddress: data.shopAddress ?? '',
        phoneNumber: data.phoneNumber ?? '',
        alternatePhoneNumber: data.alternatePhoneNumber ?? '',
        address: data.address ?? '',
        city: data.city ?? '',
        state: data.state ?? '',
        pinCode: data.pinCode ?? '',
        location: data.location ?? ''
      }));
      const bank = data.bankDetails;
      if (bank) {
        setBankForm({
          bankName: bank.bankName ?? '',
          branchState: bank.branchState ?? '',
          branchName: bank.branchName ?? '',
          accountNumber: bank.accountNumber ?? '',
          confirmAccountNumber: bank.accountNumber ?? '',
          ifscCode: bank.ifscCode ?? '',
          accountType: bank.accountType || 'Savings',
        });
        setExistingPassbookUrl(bank.passbookProofUrl ?? '');
      } else {
        setBankForm(emptySellerBankForm());
        setExistingPassbookUrl('');
      }
      setPassbookFile(null);
      setPassbookPreview((prev) => {
        if (prev && String(prev).startsWith('blob:')) URL.revokeObjectURL(prev);
        return '';
      });
      setEditSubCategoryIds(normalizeList(data.subCategoryIds) ?? []);
      setEditTertiaryCategoryIds(normalizeList(data.tertiaryCategoryIds) ?? []);
      setEditQuaternaryCategoryIds(normalizeList(data.quaternaryCategoryIds) ?? []);
    } catch (err) {
      setEditError(err.response?.data?.message || err.response?.data || 'Failed to load seller details.');
    } finally {
      setEditFetching(false);
    }
  };

  const closeEditModal = () => {
    setEditModal(false);
    setEditingSeller(null);
    setEditError(null);
    setEditSubCategoryIds([]);
    setEditTertiaryCategoryIds([]);
    setEditQuaternaryCategoryIds([]);
    setShopPhotoPreview((prev) => {
      if (prev && String(prev).startsWith('blob:')) URL.revokeObjectURL(prev);
      return '';
    });
    setBankForm(emptySellerBankForm());
    setPassbookFile(null);
    setExistingPassbookUrl('');
    setPassbookPreview((prev) => {
      if (prev && String(prev).startsWith('blob:')) URL.revokeObjectURL(prev);
      return '';
    });
  };

  const handleEditChange = (e) => {
    const { name, value } = e.target;
    setEditForm(prev => ({ ...prev, [name]: name === 'isActive' ? value === 'true' : value }));
  };

  const getApiErrorMessage = (err, fallback) => {
    const data = err?.response?.data;
    if (typeof data === 'string' && data.trim()) return data;
    if (typeof data?.title === 'string' && data.title.trim()) return data.title;
    if (typeof data?.message === 'string' && data.message.trim()) return data.message;
    if (err?.response?.status === 403) return 'You do not have permission to upload this file.';
    if (err?.response?.status === 401) return 'Please log in as Admin and try again.';
    return fallback;
  };

  const handleShopPhotoUpload = async (e) => {
    const file = e.target?.files?.[0];
    if (!file) return;
    setShopPhotoPreview((prev) => {
      if (prev && String(prev).startsWith('blob:')) URL.revokeObjectURL(prev);
      return URL.createObjectURL(file);
    });
    setShopPhotoUploading(true);
    setEditError(null);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await axios.post(`${API_URL}/ImageUpload/shop`, fd, {
        headers: getAuthHeader()
      });
      const url = res.data?.imageUrl ?? '';
      setEditForm((prev) => ({ ...prev, shopPhotoUrl: url }));
    } catch (err) {
      setEditError(getApiErrorMessage(err, 'Failed to upload shop photo.'));
    } finally {
      setShopPhotoUploading(false);
      e.target.value = '';
    }
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    if (!editForm.username.trim() || !editForm.email.trim()) {
      setEditError('Username and email are required');
      return;
    }

    setEditLoading(true);
    setEditError(null);

    try {
      const payload = {
        username: editForm.username,
        email: editForm.email,
        isActive: editForm.isActive,
        shopName: editForm.shopName || null,
        gstNumber: editForm.gstNumber || null,
        ownerName: editForm.ownerName || null,
        shopPhotoUrl: editForm.shopPhotoUrl || null,
        shopMapUrl: editForm.shopMapUrl || null,
        shopAddress: editForm.shopAddress || null,
        phoneNumber: editForm.phoneNumber || null,
        alternatePhoneNumber: editForm.alternatePhoneNumber || null,
        address: editForm.address || null,
        city: editForm.city || null,
        state: editForm.state || null,
        pinCode: editForm.pinCode || null,
        location: editForm.location || null,
        subCategoryIds: editSubCategoryIds || [],
        tertiaryCategoryIds: editTertiaryCategoryIds || [],
        quaternaryCategoryIds: editQuaternaryCategoryIds || []
      };
      if (editForm.password) payload.password = editForm.password;
      if (editForm.commissionPercent !== '') payload.commissionPercent = parseFloat(editForm.commissionPercent);
      await axios.put(`${sellersApiUrl}/${editingSeller.userId}`, payload, {
        headers: { ...getAuthHeader(), 'Content-Type': 'application/json' }
      });

      if (isSellerBankFormStarted(bankForm, passbookFile) || existingPassbookUrl) {
        const bankError = validateSellerBankForm(bankForm, passbookFile, Boolean(existingPassbookUrl));
        if (bankError) {
          setEditError(bankError);
          setEditLoading(false);
          return;
        }
        const bankFd = new FormData();
        appendSellerBankFormData(
          bankFd,
          bankForm,
          passbookFile,
          editForm.ownerName.trim() || editForm.username
        );
        await axios.post(`${sellersApiUrl}/${editingSeller.userId}/bank-details`, bankFd, {
          headers: getAuthHeader()
        });
      }
      closeEditModal();
      fetchSellers();
    } catch (err) {
      setEditError(err.response?.data || 'Failed to update seller');
    } finally {
      setEditLoading(false);
    }
  };

  // Delete functions
  const openDeleteModal = (seller) => {
    setDeletingSeller(seller);
    setDeleteModal(true);
  };

  const closeDeleteModal = () => {
    setDeleteModal(false);
    setDeletingSeller(null);
  };

  const handleDelete = async () => {
    if (!deletingSeller?.userId) return;
    setDeleteLoading(true);
    try {
      await axios.post(
        `${sellersApiUrl}/${deletingSeller.userId}/deactivate`,
        {},
        { headers: getAuthHeader() }
      );
      closeDeleteModal();
      fetchSellers();
    } catch (err) {
      const apiMessage =
        err.response?.data?.message
        || err.response?.data
        || err.message
        || 'Failed to inactivate seller';
      alert(typeof apiMessage === 'string' ? apiMessage : 'Failed to inactivate seller');
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleActivateSeller = async (seller) => {
    if (!seller?.userId) return;
    setStatusLoadingId(seller.userId);
    setError(null);
    try {
      await axios.post(
        `${sellersApiUrl}/${seller.userId}/activate`,
        {},
        { headers: getAuthHeader() }
      );
      setSellers((prev) =>
        prev.map((item) => (item.userId === seller.userId ? { ...item, isActive: true } : item))
      );
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to activate seller.');
    } finally {
      setStatusLoadingId(null);
    }
  };

  const formatDate = (dateString) => {
    if (dateString == null || dateString === '') return 'N/A';
    const d = new Date(dateString);
    if (isNaN(d.getTime())) return 'N/A';
    const year = d.getFullYear();
    if (year < 1980) return 'N/A'; // default/min date from backend when column missing
    return d.toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  if (!isLoggedIn || userRole !== 'Admin') {
    return null;
  }

  return (
    <div className="manage-sellers-wrapper">
      <Header />
      <div className="manage-sellers-container">
        <div className="page-header">
          <h1>Manage Sellers</h1>
          <button 
            type="button"
            className="add-seller-btn"
            onClick={() => navigate('/admin/add-seller')}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M12 5v14M5 12h14"/>
            </svg>
            Add New Seller
          </button>
        </div>

        <div className="search-bar">
          <input
            type="text"
            placeholder="Search by seller code, username, or email..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        {loading ? (
          <div className="loading">Loading sellers...</div>
        ) : error ? (
          <div className="error">{error}</div>
        ) : filteredSellers.length === 0 ? (
          <div className="no-sellers">
            {searchTerm ? 'No sellers match your search.' : 'No sellers found. Add your first seller!'}
          </div>
        ) : (
          <div className="sellers-table-container">
            <table className="sellers-table">
              <thead>
                <tr>
                  <th>Seller Code</th>
                  <th>Username</th>
                  <th>Email</th>
                  <th>Shop photo</th>
                  <th>Map URL</th>
                  <th>Bank KYC</th>
                  <th>Status</th>
                  <th>Category</th>
                  <th>Commission %</th>
                  <th>Created At</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredSellers.map(seller => {
                  const categoryList = normalizeList(seller.categories ?? seller.Categories);
                  const active = sellerIsActive(seller);
                  return (
                  <tr key={seller.userId} className={active ? '' : 'seller-row-inactive'}>
                    <td>{getSellerCode(seller) || seller.userId}</td>
                    <td>{seller.username}</td>
                    <td>{seller.email}</td>
                    <td className="col-shop-photo">
                      {seller.shopPhotoUrl ? (
                        <img src={getImageUrl(seller.shopPhotoUrl)} alt="" className="seller-table-photo" />
                      ) : (
                        '—'
                      )}
                    </td>
                    <td className="col-map-url">
                      {seller.shopMapUrl ? (
                        <a
                          href={/^https?:\/\//i.test(seller.shopMapUrl) ? seller.shopMapUrl : `https://${seller.shopMapUrl}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          title={seller.shopMapUrl}
                        >
                          Open map
                        </a>
                      ) : (
                        '—'
                      )}
                    </td>
                    <td>
                      <span className={`seller-status-badge ${seller.bankKycStatus === 'Verified' ? 'active' : seller.bankKycStatus === 'Pending' ? 'pending' : 'inactive'}`}>
                        {seller.bankKycStatus === 'Verified' ? 'Verified' : seller.bankKycStatus === 'Pending' ? 'Pending' : 'Not submitted'}
                      </span>
                    </td>
                    <td>
                      <span className={`seller-status-badge ${active ? 'active' : 'inactive'}`}>
                        {active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="category-cell" title={categoryList.length > 0 ? categoryList.join(', ') : undefined}>
                      {categoryList.length > 0 ? categoryList.join(', ') : '—'}
                    </td>
                    <td>{seller.commissionPercent != null ? `${seller.commissionPercent}%` : '-'}</td>
                    <td>{formatDate(seller.createdAt)}</td>
                    <td className="actions-cell">
                      <button 
                        className="edit-btn"
                        onClick={() => openEditModal(seller)}
                      >
                        Edit
                      </button>
                      {active ? (
                        <button
                          type="button"
                          className="seller-status-btn deactivate"
                          onClick={() => openDeleteModal(seller)}
                          disabled={deleteLoading || statusLoadingId === seller.userId}
                          title="Mark seller inactive and remove from shops and products"
                        >
                          Mark Inactive
                        </button>
                      ) : (
                        <button
                          type="button"
                          className="seller-status-btn activate"
                          onClick={() => handleActivateSeller(seller)}
                          disabled={statusLoadingId === seller.userId}
                          title="Allow this seller to log in again"
                        >
                          {statusLoadingId === seller.userId ? 'Saving...' : 'Mark Active'}
                        </button>
                      )}
                    </td>
                  </tr>
                );})}
              </tbody>
            </table>
          </div>
        )}

        <div className="total-count">
          Total Sellers: {filteredSellers.length}
        </div>
      </div>

      {/* Edit Modal */}
      {editModal && (
        <div className="modal-overlay" onClick={closeEditModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2>Edit Seller</h2>
            {editError && <div className="modal-error">{editError}</div>}
            <form onSubmit={handleEditSubmit}>
              <div className="form-group">
                <label>Username</label>
                <input
                  type="text"
                  name="username"
                  value={editForm.username}
                  onChange={handleEditChange}
                  disabled={editLoading}
                />
              </div>
              <div className="form-group">
                <label>Email</label>
                <input
                  type="email"
                  name="email"
                  value={editForm.email}
                  onChange={handleEditChange}
                  disabled={editLoading}
                />
              </div>
              <div className="form-group">
                <label>Seller Status</label>
                <select
                  name="isActive"
                  value={String(editForm.isActive)}
                  onChange={handleEditChange}
                  disabled={editLoading}
                >
                  <option value="true">Active</option>
                  <option value="false">Inactive</option>
                </select>
              </div>
              <div className="form-group">
                <label>New Password (leave blank to keep current)</label>
                <input
                  type="password"
                  name="password"
                  value={editForm.password}
                  onChange={handleEditChange}
                  placeholder="Enter new password"
                  disabled={editLoading}
                />
              </div>
              <div className="form-group">
                <label>Commission % (Admin receives from seller transactions)</label>
                <input
                  type="number"
                  name="commissionPercent"
                  value={editForm.commissionPercent}
                  onChange={handleEditChange}
                  placeholder="e.g. 10"
                  min="0"
                  max="100"
                  step="0.5"
                  disabled={editLoading}
                />
              </div>
              <h3 className="edit-section-title">Shop &amp; contact details</h3>
              <div className="form-row-two">
                <div className="form-group">
                  <label>Shop name</label>
                  <input type="text" name="shopName" value={editForm.shopName} onChange={handleEditChange} placeholder="Shop name" disabled={editLoading} />
                </div>
                <div className="form-group">
                  <label>GST number</label>
                  <input type="text" name="gstNumber" value={editForm.gstNumber} onChange={handleEditChange} placeholder="GST number" disabled={editLoading} />
                </div>
              </div>
              <div className="form-group">
                <label>Owner name</label>
                <input type="text" name="ownerName" value={editForm.ownerName} onChange={handleEditChange} placeholder="Owner name" disabled={editLoading} />
              </div>
              <SellerBankKycFields
                values={bankForm}
                onChange={setBankForm}
                passbookFile={passbookFile}
                passbookPreview={passbookPreview}
                existingPassbookUrl={existingPassbookUrl}
                holderName={editForm.ownerName.trim() || editForm.username}
                disabled={editLoading || editFetching}
                onPassbookChange={(e) => {
                  const file = e.target?.files?.[0];
                  if (!file) return;
                  if (file.size > 5 * 1024 * 1024) {
                    setEditError('Passbook file must be 5MB or smaller.');
                    return;
                  }
                  setPassbookPreview((prev) => {
                    if (prev && String(prev).startsWith('blob:')) URL.revokeObjectURL(prev);
                    return URL.createObjectURL(file);
                  });
                  setPassbookFile(file);
                }}
              />
              <div className="form-group">
                <label>Shop photo</label>
                <div className="upload-area-inline">
                  <input type="file" accept="image/jpeg,image/png,image/gif,image/webp" onChange={handleShopPhotoUpload} disabled={editLoading || shopPhotoUploading} />
                  {(shopPhotoPreview || editForm.shopPhotoUrl) ? (
                    <div className="preview-inline">
                      <img src={shopPhotoPreview || getImageUrl(editForm.shopPhotoUrl)} alt="Shop preview" />
                      <span className="uploaded-tag">{shopPhotoUploading ? 'Uploading…' : (editForm.shopPhotoUrl ? 'Uploaded' : 'Preview')}</span>
                    </div>
                  ) : (
                    <span className="upload-hint">Click to upload shop photo (JPG, PNG)</span>
                  )}
                </div>
              </div>
              <div className="form-group">
                <label>Shop map URL</label>
                <input
                  type="text"
                  name="shopMapUrl"
                  value={editForm.shopMapUrl}
                  onChange={handleEditChange}
                  placeholder="Paste Google Maps link"
                  disabled={editLoading}
                />
                <small className="form-hint">Open Google Maps, share the shop location, and paste the link here.</small>
              </div>
              <div className="form-group">
                <label>Shop address</label>
                <textarea name="shopAddress" value={editForm.shopAddress} onChange={handleEditChange} placeholder="Full shop address" rows={2} disabled={editLoading} />
              </div>
              <div className="form-row-two">
                <div className="form-group">
                  <label>Contact number</label>
                  <input type="tel" name="phoneNumber" value={editForm.phoneNumber} onChange={handleEditChange} placeholder="Contact number" disabled={editLoading} />
                </div>
                <div className="form-group">
                  <label>Alternate contact</label>
                  <input type="tel" name="alternatePhoneNumber" value={editForm.alternatePhoneNumber} onChange={handleEditChange} placeholder="Alternate number" disabled={editLoading} />
                </div>
              </div>
              <div className="form-group">
                <label>Location (area / locality)</label>
                <input type="text" name="location" value={editForm.location} onChange={handleEditChange} placeholder="e.g. MG Road, Sector 18" disabled={editLoading} />
              </div>
              <div className="form-group">
                <label>Address (street, building)</label>
                <input type="text" name="address" value={editForm.address} onChange={handleEditChange} placeholder="Address line" disabled={editLoading} />
              </div>
              <div className="form-row-three">
                <div className="form-group">
                  <label>City</label>
                  <input type="text" name="city" value={editForm.city} onChange={handleEditChange} placeholder="City" disabled={editLoading} />
                </div>
                <div className="form-group">
                  <label>State</label>
                  <input type="text" name="state" value={editForm.state} onChange={handleEditChange} placeholder="State" disabled={editLoading} />
                </div>
                <div className="form-group">
                  <label>Pin code</label>
                  <input type="text" name="pinCode" value={editForm.pinCode} onChange={handleEditChange} placeholder="Pin code" disabled={editLoading} />
                </div>
              </div>
              <div className="form-group categories-edit">
                <label>Categories this seller can sell in</label>
                <small className="form-hint">Select at SubCategory, Tertiary, or Quaternary level. You can update the selection.</small>
                {editFetching ? (
                  <p className="modal-loading-msg">Loading categories...</p>
                ) : (
                  <div className="edit-modal-category-list">
                    {flattenCategorySelections(categoryTree, normalizeList).map((item) => {
                      const checked = item.type === 'sub' ? editSubCategoryIds.includes(item.id)
                        : item.type === 'tertiary' ? editTertiaryCategoryIds.includes(item.id)
                          : editQuaternaryCategoryIds.includes(item.id);
                      const setter = item.type === 'sub' ? setEditSubCategoryIds : item.type === 'tertiary' ? setEditTertiaryCategoryIds : setEditQuaternaryCategoryIds;
                      const typeLabel = item.type === 'sub' ? 'Sub' : item.type === 'tertiary' ? 'Tert' : 'Quat';
                      return (
                        <label key={`${item.type}-${item.id}`} className="edit-category-check">
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setter(prev => [...prev, item.id]);
                              } else {
                                setter(prev => prev.filter(id => id !== item.id));
                              }
                            }}
                            disabled={editLoading}
                          />
                          <span className="edit-category-path"><span className="category-type-badge">{typeLabel}</span> {item.path}</span>
                        </label>
                      );
                    })}
                    {flattenCategorySelections(categoryTree, normalizeList).length === 0 && !editFetching && (
                      <p className="no-categories-msg">No categories yet. Add them under Admin → Manage Categories.</p>
                    )}
                  </div>
                )}
              </div>
              <div className="modal-buttons">
                <button type="button" onClick={closeEditModal} disabled={editLoading}>
                  Cancel
                </button>
                <button type="submit" className="save-btn" disabled={editLoading}>
                  {editLoading ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Deactivate Modal */}
      {deleteModal && (
        <div className="modal-overlay" onClick={closeDeleteModal}>
          <div className="modal-content delete-modal" onClick={(e) => e.stopPropagation()}>
            <h2>Mark Seller Inactive</h2>
            <p>
              Mark seller <strong>{deletingSeller?.username}</strong> as inactive?
            </p>
            <p className="warning">
              They will be hidden from shops, product listings, category maps, and assignment dropdowns.
              They will not be able to log in. Past orders and commissions are kept for records.
            </p>
            <div className="modal-buttons">
              <button type="button" onClick={closeDeleteModal} disabled={deleteLoading}>
                Cancel
              </button>
              <button 
                type="button" 
                className="confirm-delete-btn"
                onClick={handleDelete}
                disabled={deleteLoading}
              >
                {deleteLoading ? 'Saving...' : 'Mark Inactive'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ManageSellersPage;
